/* @flow */
import { debounce } from 'lodash';
import Form from 'components/Form.jsx';
import FormInput from 'components/FormInput.jsx';
import Group from 'entities/Group';
import GroupSchoolSelect from 'groups/components/GroupSchoolSelect.jsx';
import GroupStore from 'groups/stores/GroupStore';
import PrependedTextInput from 'components/PrependedTextInput.jsx';
import SettingsStore from 'stores/SettingsStore';
import * as React from 'react';
import Organization from 'entities/Organization';
import createChainedFunction from 'utils/createChainedFunction';
import errorLogger from 'utils/errorLogger';

let cfg = {};

type Props = {
  onFormValid: (name: string, code: string, orgId?: ?string) => void,
  onFormInvalid: () => void,
  onValidNameChange?: ?(name: string) => void,
  onCodeChange?: ?(code: string) => void,
  name: string,
  code: string,
  currentOrganization?: ?Organization,
  group?: ?Group,
  hasChildren: boolean,
  showOrg: boolean,
};

type State = {
  classNameError: ?string,
  classCodeError: ?string,
  verifyingCodeUniqueness: boolean,
};

class GroupForm extends React.Component<Props, State> {
  static defaultProps = {
    name: '',
    code: '',
    hasChildren: false,
    showOrg: true,
  };

  state: State = {
    classCodeError: null,
    classNameError: null,
    verifyingCodeUniqueness: false,
  };
  componentWillMount() {
    cfg = {
      GROUP_CODE_MAX_LENGTH: SettingsStore.getMaxClassCodeLength(),
      GROUP_CODE_MIN_LENGTH: SettingsStore.getMinClassCodeLength(),
      GROUP_NAME_MAX_LENGTH: SettingsStore.getMaxClassNameLength(),
      GROUP_NAME_MIN_LENGTH: SettingsStore.getMinClassNameLength(),
      GROUP_CODE_REGEX: /^[a-zA-Z0-9\-]+$/,
    };
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  _isMounted: boolean = false;

  isMounted() {
    return this._isMounted;
  }

  onNameChange = (event: SyntheticEvent<>) => {
    // Clear previous errors
    this.setState({ classNameError: null });
    // $FlowFixMe
    const name = event.currentTarget.value;

    if (
      this.props.name &&
      name === this.props.name &&
      this.props.code === this.getCurrentCodeValue()
    ) {
      this.props.onFormInvalid();
      return;
    }

    if (!this.nameIsValid(name)) {
      return;
    }

    if (this.hasDuplicateGroupName(name)) {
      this.setState({
        classNameError: 'You already have a class by that name. Please pick another.',
      });
      this.props.onFormInvalid();
      return;
    }

    if (this.props.onValidNameChange) {
      this.props.onValidNameChange(name);
    }
    const code = this.getCurrentCodeValue();
    if (this.nameIsValid(name) && this.codeIsValid(code) && !this.state.classCodeError) {
      this.props.onFormValid(name, code);
    }
  };
  onCodeChange = (event: SyntheticEvent<>) => {
    // Notify parent that the form is invalid, so it can disable submit button
    this.props.onFormInvalid();

    // Clear previous errors
    this.setState({ classCodeError: null });
    this.setState({ verifyingCodeUniqueness: false });

    // We must trim the code here to match the behavior of Formsy's length validators
    // $FlowFixMe
    const code = event.currentTarget.value.trim();

    if (this.props.code && code.length === 0) {
      this.setState({ classCodeError: 'Class code is required.' });
      return;
    }

    // When editing an existing class, and the code is the same as existing code
    if (this.props.code && code === this.props.code) {
      // If the name has also changed to something valid
      if (
        this.props.name !== this.getCurrentNameValue() &&
        this.nameIsValid(this.getCurrentNameValue()) &&
        !this.state.classNameError
      ) {
        this.props.onFormValid(this.getCurrentNameValue(), code);
      } else {
        this.props.onFormInvalid();
      }
      return;
    }

    // If the code is empty, and the current name is valid, call the onFormValid handler
    // and skip other checks on the code (but only if this isn't an existing class)
    // This let's us generate a code for a new class created with a blank code.
    if (
      !code &&
      !this.props.code &&
      this.nameIsValid(this.getCurrentNameValue()) &&
      !this.state.classNameError
    ) {
      this.props.onFormValid(this.getCurrentNameValue(), code);
      return;
    }

    // Otherwise, check if the code is valid with client-side checks
    if (code && this.codeIsValid(code)) {
      // Turn on the class code input's loading indicator
      this.setState({ verifyingCodeUniqueness: true });

      // Then call the server to check if the code is unique
      this.validateGroupCodeUniqueness(code);
    }
  };
  onSchoolChange = (orgId: ?string) => {
    const code = this.getCurrentCodeValue();
    const name = this.getCurrentNameValue();
    if (this.nameIsValid(name) && this.codeIsValid(code) && !this.state.classCodeError) {
      this.props.onFormValid(name, code, orgId);
    }
  };
  getCurrentNameValue = () => {
    // This is the odd roundabout method for fetching the value of a FormInput
    return this.refs.name.refs.children.getValue();
  };
  getCurrentCodeValue = () => {
    return this.refs.code.refs.children.getValue();
  };
  getOrganizationForSubmit = () => {
    return this.refs.org.getOrganizationForSubmit();
  };
  hasDuplicateGroupName = (name: string) => {
    return this.props.name
      ? GroupStore.getOwnedGroupForName(name) &&
          name.toLowerCase() !== this.props.name.toLowerCase()
      : !!GroupStore.getOwnedGroupForName(name);
  };
  nameIsValid = (name: string) => {
    return (
      name && name.length >= cfg.GROUP_NAME_MIN_LENGTH && name.length <= cfg.GROUP_NAME_MAX_LENGTH
    );
  };
  codeIsValid = (code: string) => {
    let valid =
      code.length >= cfg.GROUP_CODE_MIN_LENGTH &&
      code.length <= cfg.GROUP_CODE_MAX_LENGTH &&
      cfg.GROUP_CODE_REGEX.test(code);

    if (!this.props.code) {
      valid = valid || code.length === 0;
    }
    return valid;
  };
  changeCodeTo = (value: string) => {
    if (!this.getCurrentCodeValue()) {
      this.refs.code.refs.children.changeValue(value);
    }
  };
  validateGroupCodeUniqueness = debounce((code: string) => {
    if (!this.isMounted()) {
      return;
    }

    this.props.onFormInvalid();
    Group.validateCodeUniqueness(code)
      .then(codes => {
        // XXX move promise out of this view
        if (!this.isMounted()) {
          return;
        }

        // Disable the class code input's loading indicator
        this.setState({ verifyingCodeUniqueness: false });

        // If the code has changed since we dispatched this request,
        // then result is no longer valid, so don't overwrite the error message
        // This prevents these responses from overwriting length validation results.
        if (code !== this.getCurrentCodeValue()) {
          return;
        }

        // The server returns an array of suggested code when the code passed
        // to it was not unique (i.e. a duplicate)
        if (codes) {
          this.setState({
            classCodeError: `
                Oops! This code is unavailable. Here are some available codes:
                @${codes[0]}, @${codes[1]}, or @${codes[2]}.`,
          });
        } else if (
          this.nameIsValid(this.getCurrentNameValue()) &&
          !this.state.classNameError &&
          !this.state.classCodeError
        ) {
          // If no codes are returned, the code passed in was unique
          // So if the current name is also valid, call the onFormValid handler
          this.props.onFormValid(this.getCurrentNameValue(), this.getCurrentCodeValue());
        }
      })
      .catch(errorLogger('GroupForm validateCodeUniqueness failed'));
  }, 300);
  render() {
    return (
      <div>
        <Form onInvalid={this.props.onFormInvalid}>
          <FormInput
            label="Class name"
            required
            autofocus
            ref="name"
            name="class_name"
            placeholder="e.g. Math 101, Soccer Team, Chess Club"
            value={this.props.name}
            onChange={this.onNameChange}
            errorMessage={this.state.classNameError}
            className="GroupForm-groupName"
            validations={{
              maxLength: cfg.GROUP_NAME_MAX_LENGTH,
              minLength: cfg.GROUP_NAME_MIN_LENGTH,
            }}
            validationErrors={{
              minLength: `The name must be at least ${cfg.GROUP_NAME_MIN_LENGTH} characters long.`,
              maxLength: `The name cannot be over ${cfg.GROUP_NAME_MAX_LENGTH} characters long.`,
              isDefaultRequiredValue: 'Class name is required.',
            }}
          />
          <FormInput
            label="Class code"
            prepend="@"
            name="name"
            ref="code"
            loading={this.state.verifyingCodeUniqueness}
            component={PrependedTextInput}
            value={this.props.code}
            validationError="Class code is required"
            validations={{
              maxLength: cfg.GROUP_CODE_MAX_LENGTH,
              minLength: cfg.GROUP_CODE_MIN_LENGTH,
              matchRegexp: cfg.GROUP_CODE_REGEX,
            }}
            onChange={createChainedFunction(this.onCodeChange, this.props.onCodeChange)}
            errorMessage={this.state.classCodeError}
            validationErrors={{
              minLength: `The code must be at least ${cfg.GROUP_CODE_MIN_LENGTH} characters long.`,
              maxLength: `Class code must be ${cfg.GROUP_CODE_MAX_LENGTH} characters or less.`,
              matchRegexp: 'The code can only contain characters a-Z and -',
            }}
            className="GroupForm-groupCode"
          />
          {this.props.showOrg && (
            <GroupSchoolSelect
              group={this.props.group}
              hideUnaffiliated={this.props.hasChildren}
              initialOrganizationId={
                this.props.currentOrganization ? this.props.currentOrganization.id : null
              }
              onSchoolChange={this.onSchoolChange}
              input="select"
              ref="org"
            />
          )}
        </Form>
      </div>
    );
  }
}

export default GroupForm;
